import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import GameCard, { GameType } from '../components/GameCard';
import BottomNavBar from '../components/BottomNavBar';
import SEOMeta from '../components/SEOMeta';
import { useAuth } from '../contexts/AuthContext';
import { useDongengFavorites } from '../hooks/useDongengFavorites';

interface DongengVideo { id: string; title: string; thumbnail: string; published: string; }

/* ── Styled ── */

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  padding-bottom: 96px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

/* Header */
const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #f1f5f9;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #2DBCAF;
  padding: 0;
  .material-symbols-outlined { font-size: 24px; }
`;

const HeaderTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  flex: 1;
`;

/* Title Bar */
const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px 12px;
`;

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
`;

const TotalBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(45,188,175,0.1);
  border: 1px solid rgba(45,188,175,0.2);
  border-radius: 9999px;
  padding: 4px 12px;
  .count {
    font-size: 14px;
    font-weight: 800;
    color: #2DBCAF;
  }
  .label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
  }
`;

/* Search */
const SearchSection = styled.div`
  padding: 0 16px 16px;
`;

const SearchWrapper = styled.label`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 14px;
  color: #2DBCAF;
  display: flex;
  .material-symbols-outlined { font-size: 22px; }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 16px 0 46px;
  background: white;
  border: 2px solid rgba(45,188,175,0.15);
  border-radius: 14px;
  font-size: 15px;
  font-family: inherit;
  color: #0f172a;
  outline: none;
  transition: border 0.2s, box-shadow 0.2s;
  &::placeholder { color: #94a3b8; }
  &:focus {
    border-color: #2DBCAF;
    box-shadow: 0 0 0 4px rgba(45,188,175,0.1);
  }
`;

/* Grid */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 0 16px;
`;

/* Empty */
const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 32px;
  text-align: center;
  gap: 12px;
  .material-symbols-outlined {
    font-size: 56px;
    color: #cbd5e1;
    font-variation-settings: 'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 48;
  }
  h3 {
    font-size: 16px;
    font-weight: 700;
    color: #475569;
    margin: 0;
  }
  p {
    font-size: 13px;
    color: #94a3b8;
    margin: 0;
    line-height: 1.5;
  }
`;

const ExploreBtn = styled.button`
  margin-top: 8px;
  padding: 12px 28px;
  background: #2DBCAF;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(45,188,175,0.3);
  &:active { transform: scale(0.97); }
`;

/* Not logged in */
const LoginBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 32px;
  text-align: center;
  gap: 12px;
  .material-symbols-outlined {
    font-size: 56px;
    color: #2DBCAF;
    font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 48;
  }
  h3 { font-size: 16px; font-weight: 700; color: #475569; margin: 0; }
  p  { font-size: 13px; color: #94a3b8; margin: 0; line-height: 1.5; }
`;

const LoginBtn = styled.button`
  margin-top: 8px;
  padding: 12px 28px;
  background: #2DBCAF;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(45,188,175,0.3);
`;

/* Dongeng Section */
const SectionTitle = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 16px 12px;
  h2 { font-size: 18px; font-weight: 800; color: #0f172a; margin: 0; }
  a { font-size: 13px; font-weight: 700; color: #2DBCAF; cursor: pointer; }
  gap: 8px;
`;

const DongengGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 0 16px;
`;

const DongengCard = styled.div`
  border-radius: 12px; overflow: hidden;
  background: #fff; cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: transform 0.15s;
  &:hover { transform: translateY(-2px); }
`;

const DongengThumb = styled.div<{ $src: string }>`
  width: 100%; aspect-ratio: 9/16;
  background: url(${p => p.$src}) center/cover no-repeat #e2e8f0;
  position: relative;
`;

const DongengPlayOverlay = styled.div`
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,0.15);
  .material-symbols-outlined { color: rgba(255,255,255,0.9); font-size: 24px; }
`;

const DongengCardTitle = styled.p`
  font-size: 10px; font-weight: 700; color: #1e293b;
  margin: 0; padding: 6px 8px 8px; line-height: 1.35;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;

const Divider = styled.hr`
  border: none; border-top: 1px solid #e2e8f0; margin: 8px 16px 0;
`;

/* ── Component ── */

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useDongengFavorites();
  const [games, setGames] = useState<GameType[]>([]);
  const [loading, setLoading] = useState(false);
  const [dongengFavIds, setDongengFavIds] = useState<string[]>([]);
  const [allDongeng, setAllDongeng] = useState<DongengVideo[]>([]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    // Fetch game favorites
    fetch('/api/favorites')
      .then(r => r.json())
      .then(data => setGames(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));

    // Fetch dongeng favorite IDs
    fetch('/api/dongeng/favorites')
      .then(r => r.json())
      .then(d => { if (d.ok) setDongengFavIds(d.favorites); })
      .catch(() => {});

    // Fetch all dongeng for metadata (title/thumbnail)
    fetch('/api/dongeng/videos')
      .then(r => r.json())
      .then(d => { if (d.ok) setAllDongeng(d.videos); })
      .catch(() => {});
  }, [user]);

  const favDongeng = allDongeng.filter(v => dongengFavIds.includes(v.id));

  return (
    <>
      <SEOMeta
        title="Game Favorit Saya - KinderGame.id"
        description="Daftar game edukasi favorit pilihan kamu di KinderGame.id"
        path="/favorites"
      />
      <PageWrapper>
        <Header>
          <BackBtn onClick={() => navigate(-1)}>
            <span className="material-symbols-outlined">arrow_back</span>
          </BackBtn>
          <HeaderTitle>Favorit</HeaderTitle>
        </Header>

        {!user ? (
          <LoginBox>
            <span className="material-symbols-outlined">lock</span>
            <h3>Masuk untuk melihat favorit</h3>
            <p>Login dengan Google untuk menyimpan dan melihat game favoritmu.</p>
            <LoginBtn onClick={() => { const returnTo = encodeURIComponent(window.location.pathname + window.location.search); window.location.href = '/api/auth/google?returnTo=' + returnTo; }}>
              Login dengan Google
            </LoginBtn>
          </LoginBox>
        ) : (
          <>
            {/* Game Favorit */}
            <TitleBar>
              <PageTitle>🎮 Game Favorit</PageTitle>
              <TotalBadge>
                <span className="count">{games.length}</span>
                <span className="label">game</span>
              </TotalBadge>
            </TitleBar>

            {loading ? (
              <EmptyBox>
                <span className="material-symbols-outlined">hourglass_top</span>
                <h3>Memuat...</h3>
              </EmptyBox>
            ) : games.length === 0 ? (
              <EmptyBox>
                <span className="material-symbols-outlined">favorite</span>
                <h3>Belum ada game favorit</h3>
                <p>Tap ikon ❤️ di game mana saja untuk menyimpan sebagai favorit.</p>
                <ExploreBtn onClick={() => navigate('/games')}>Jelajahi Game</ExploreBtn>
              </EmptyBox>
            ) : (
              <Grid>
                {games.map(game => (
                  <GameCard key={game._id} game={game} />
                ))}
              </Grid>
            )}

            {/* Dongeng Favorit */}
            <Divider />
            <SectionTitle>
              <h2>📖 Dongeng Favorit</h2>
              <TotalBadge>
                <span className="count">{favDongeng.length}</span>
                <span className="label">dongeng</span>
              </TotalBadge>
            </SectionTitle>

            {favDongeng.length === 0 ? (
              <EmptyBox>
                <span className="material-symbols-outlined">auto_stories</span>
                <h3>Belum ada dongeng favorit</h3>
                <p>Tap ikon ❤️ di video dongeng untuk menyimpan sebagai favorit.</p>
                <ExploreBtn onClick={() => navigate('/dongeng')}>Jelajahi Dongeng</ExploreBtn>
              </EmptyBox>
            ) : (
              <DongengGrid>
                {favDongeng.map(v => (
                  <DongengCard key={v.id} onClick={() => navigate('/dongeng', { state: { videoId: v.id } })}>
                    <DongengThumb $src={v.thumbnail}>
                      <DongengPlayOverlay>
                        <span className="material-symbols-outlined">play_arrow</span>
                      </DongengPlayOverlay>
                    </DongengThumb>
                  </DongengCard>
                ))}
              </DongengGrid>
            )}
          </>
        )}
      </PageWrapper>
      <BottomNavBar />
    </>
  );
}
